/**
 * Job title ("Role") rules for provider user accounts.
 *
 * Role is the free-text job title that appears on letters and reports
 * (for example "Clinical Psychologist"). It is not the account type
 * (Clinician, Support, Org. Admin) and it is not a registration number.
 *
 * Used when adding or editing an Assessment provider user. RMS invites
 * do not use this helper. Registration body and number stay optional.
 *
 * The same limits are enforced on the server in
 * python-functions/functions/provider_role.py. Keep the maximum length
 * and the required-field sentence in step with that file.
 */

export const MAX_PROVIDER_ROLE_LENGTH = 120;

export const PROVIDER_ROLE_REQUIRED_MESSAGE =
  'Enter a role. This is the job title that appears on letters and reports.';

/**
 * Check a job title and return the trimmed value when it is acceptable.
 *
 * @param {unknown} value Raw input from a form field.
 * @returns {{ ok: boolean, role: string, message: string }}
 *   ok is false when the value is missing, blank, multi-line, or too long.
 *   message is empty when ok is true, and is safe to show next to the field.
 */
export function normaliseProviderRole(value) {
  if (typeof value !== 'string') {
    return {
      ok: false,
      role: '',
      message: value == null ? PROVIDER_ROLE_REQUIRED_MESSAGE : 'Role must be text.',
    };
  }
  // Strip first so a pasted trailing newline does not count as a second
  // line, and a field full of spaces is simply "missing".
  const role = value.trim();
  if (!role) {
    return { ok: false, role: '', message: PROVIDER_ROLE_REQUIRED_MESSAGE };
  }
  // A newline or other control character left in the middle would break
  // the signature line on a letter.
  if (/[\u0000-\u001F\u007F]/.test(role)) {
    return { ok: false, role: '', message: 'Role must be a single line of text.' };
  }
  if (role.length > MAX_PROVIDER_ROLE_LENGTH) {
    return {
      ok: false,
      role: '',
      message: `Role must be ${MAX_PROVIDER_ROLE_LENGTH} characters or fewer.`,
    };
  }
  return { ok: true, role, message: '' };
}
